"use client";

import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@retailos/core";

type ButtonVariant = "primary" | "secondary" | "danger" | "ghost" | "outline" | "ai";
type ButtonSize = "sm" | "md" | "lg" | "xl";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: "bg-white text-black shadow-sm hover:bg-white/90 hover:shadow-md active:bg-white/80 active:scale-[0.98]",
  secondary: "bg-surface-2 text-white border border-surface-border-2 hover:bg-surface-border-2 hover:border-white/20 active:bg-surface-border active:scale-[0.98]",
  danger: "bg-danger text-white shadow-sm hover:bg-red-500 active:bg-red-600 active:scale-[0.98]",
  ghost: "bg-transparent text-text-muted hover:bg-white/5 hover:text-white active:scale-[0.98]",
  outline: "border border-surface-border-2 text-white/80 hover:bg-white/5 hover:text-white hover:border-white/30 active:scale-[0.98]",
  ai: "bg-accent/15 text-accent border border-accent/25 hover:bg-accent/25 active:scale-[0.98] ai-glow",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "px-3 py-2 text-sm min-h-[36px]",
  md: "px-4 py-2.5 text-sm min-h-[44px]",
  lg: "px-6 py-3 text-base min-h-[48px]",
  xl: "px-8 py-4 text-lg min-h-[56px] font-semibold",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "primary", size = "md", loading, disabled, children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(
          "inline-flex items-center justify-center gap-2 rounded-xl font-medium transition-all duration-200 cursor-pointer select-none",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          "disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100",
          variantClasses[variant],
          sizeClasses[size],
          className,
        )}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        )}
        {children}
      </button>
    );
  },
);

Button.displayName = "Button";
